import type { ModelConfig } from '@ownyou/ab-testing';
import { cn } from '@ownyou/ui-design-system';
import {
  getRouteFitState,
  isLlmRouteExecutableProvider,
  normalizeLlmProviderId,
  type LlmProviderId,
  type ModelRouteDefinition,
  type ModelRouteId,
  type ModelRouteSelection,
} from '../../lib/llm-routing';

interface RouteModelPickerCardProps {
  route: ModelRouteDefinition;
  models: ModelConfig[];
  providers: LlmProviderId[];
  providerNames: Record<string, string>;
  routeProvider: LlmProviderId | '';
  selection: ModelRouteSelection | undefined;
  modelsLoading: boolean;
  formatPricing: (model: { pricing?: { inputPer1M: number; outputPer1M: number } | null }) => string;
  setRouteProvider: (routeId: ModelRouteId, provider: LlmProviderId | '') => void;
  setRouteSelection: (routeId: ModelRouteId, selection: { provider: string; model: string }) => void;
  clearRouteSelection: (routeId: ModelRouteId) => void;
}

export function RouteModelPickerCard({
  route,
  models,
  providers,
  providerNames,
  routeProvider,
  selection,
  modelsLoading,
  formatPricing,
  setRouteProvider,
  setRouteSelection,
  clearRouteSelection,
}: RouteModelPickerCardProps) {
  const selectionMatchesProvider = Boolean(
    selection &&
    routeProvider &&
    selection.provider === routeProvider,
  );
  const providerExecutable = !routeProvider || isLlmRouteExecutableProvider(routeProvider);
  const modelsForProvider = routeProvider
    ? models.filter((model) => normalizeLlmProviderId(model.provider) === routeProvider)
    : [];
  const selectedModelIsUnavailable = Boolean(
    selectionMatchesProvider &&
    selection &&
    !modelsForProvider.some((model) => model.model === selection.model),
  );
  const selectedModel = selectionMatchesProvider && selection
    ? modelsForProvider.find((model) => model.model === selection.model) ?? null
    : null;
  const fitState = getRouteFitState(route, routeProvider, selectedModel, selectedModelIsUnavailable);

  return (
    <div
      data-testid={`model-route-${route.id}`}
      data-route-id={route.id}
      data-route-policy-version={route.routePolicyVersion}
      data-prompt-version={route.promptVersion}
      data-output-schema-version={route.outputSchemaVersion}
      data-validation-policy-kind={route.validationPolicy.kind}
      data-route-selection-state={selection ? 'explicit' : 'default'}
      data-route-fit-state={fitState.state}
      className="p-3 bg-neutral-50 rounded-lg border border-neutral-200"
    >
      <div className="mb-2 flex flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <label className="font-medium text-neutral-900 text-sm block mb-0.5">
            {route.label}
          </label>
          <p className="text-xs text-neutral-500">{route.description}</p>
        </div>
        <div className="flex flex-wrap gap-1">
          <span
            data-testid={`route-selection-state-${route.id}`}
            className={cn(
              'rounded-md px-2 py-1 text-xs font-medium',
              selection ? 'bg-emerald-50 text-emerald-700' : 'bg-neutral-200 text-neutral-700',
            )}
          >
            {selection ? 'Explicit' : 'Default'}
          </span>
          <span
            data-testid={`route-fit-state-${route.id}`}
            className={cn('rounded-md px-2 py-1 text-xs font-medium', fitState.className)}
          >
            {fitState.label}
          </span>
        </div>
      </div>
      <p className="mb-2 text-xs text-neutral-500">
        Default selector: {route.defaultSelector?.strategy ?? 'route default'}.
        {route.modelFitPolicy?.privacyPosture ? ` Privacy: ${route.modelFitPolicy.privacyPosture}.` : ''}
        {fitState.detail ? ` ${fitState.detail}` : ''}
      </p>
      <div className="grid grid-cols-2 gap-2">
        <select
          data-testid={`model-provider-${route.id}`}
          value={routeProvider}
          onChange={(event) => {
            setRouteProvider(route.id, event.target.value as LlmProviderId | '');
          }}
          className="w-full p-2 bg-white border border-neutral-200 rounded-lg text-sm text-neutral-900"
        >
          <option value="">Select provider...</option>
          {providers.map((provider) => {
            const executable = isLlmRouteExecutableProvider(provider);
            return (
              <option key={provider} value={provider} disabled={!executable}>
                {providerNames[provider] || provider}{executable ? '' : ' (blocked)'}
              </option>
            );
          })}
        </select>
        <select
          data-testid={`model-select-${route.id}`}
          value={selectionMatchesProvider && selection ? selection.model : ''}
          onChange={(event) => {
            const model = event.target.value;
            if (!model) {
              clearRouteSelection(route.id);
              return;
            }
            if (!routeProvider || !isLlmRouteExecutableProvider(routeProvider)) return;
            setRouteSelection(route.id, { provider: routeProvider, model });
          }}
          disabled={!routeProvider || !providerExecutable || modelsLoading}
          className="w-full p-2 bg-white border border-neutral-200 rounded-lg text-sm text-neutral-900 disabled:opacity-50"
        >
          <option value="">Select model...</option>
          {selectedModelIsUnavailable && selection && (
            <option value={selection.model}>
              Unavailable: {selection.model}
            </option>
          )}
          {modelsForProvider.map((model) => (
            <option key={model.model} value={model.model}>
              {model.displayName}{formatPricing(model)}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}
